import { useCallback, useEffect, useRef, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { DEFAULT_ROLE_SORT, listRoles } from "./roles.gateway";
import type { RoleSortField, RoleSortOrder, RolesQueryState } from "./roles.types";

export const SEARCH_DEBOUNCE_MS = 300;

export const ROLES_FORBIDDEN_MESSAGE = "Você não tem permissão para consultar os perfis do Hub.";
export const ROLES_ERROR_MESSAGE = "Não foi possível carregar os perfis.";

const INITIAL_STATE: RolesQueryState = {
  status: "loading",
  roles: [],
  total: 0,
  errorMessage: null,
};

export interface UseRolesParams {
  search: string;
  permissaoIds: string[];
  page: number;
  pageSize: number;
  sortField?: RoleSortField;
  sortOrder?: RoleSortOrder;
}

interface UseRolesResult extends RolesQueryState {
  reload: () => void;
}

export function useRoles({
  search,
  permissaoIds,
  page,
  pageSize,
  sortField = DEFAULT_ROLE_SORT.field,
  sortOrder = DEFAULT_ROLE_SORT.order,
}: UseRolesParams): UseRolesResult {
  const [state, setState] = useState<RolesQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);

  const term = search.trim();
  // Chave estável: o array de permissões muda de identidade a cada render da tela.
  const permissoesKey = permissaoIds.join(",");

  useEffect(() => {
    // Debounce só na digitação; carga inicial, filtro, paginação e retry são imediatos.
    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    let cancelled = false;
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        listRoles({
          pesquisa: term,
          permissaoIds: permissoesKey ? permissoesKey.split(",") : [],
          pagina: page,
          tamanhoPagina: pageSize,
          ordenarPor: sortField,
          ordem: sortOrder,
          signal: controller.signal,
        })
          .then(({ roles, total }) => {
            if (cancelled) return;
            setState({ status: "success", roles, total, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;

            const isForbidden = isAPIError(error) && error.status === 403;
            setState({
              status: isForbidden ? "forbidden" : "error",
              roles: [],
              total: 0,
              errorMessage: isForbidden
                ? ROLES_FORBIDDEN_MESSAGE
                : getErrorMessage(error) || ROLES_ERROR_MESSAGE,
            });
          });
      },
      shouldDebounce ? SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, permissoesKey, page, pageSize, sortField, sortOrder, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
